/**
 * The auditor advisory shape and content-addressed identity (04-llm-role.md
 * §5.1). The deterministic rule engine is the floor; the LLM augmentation
 * layer (§5.4) is additive and lands later.
 *
 * Advisory ids are CONTENT-ADDRESSED: `hash(rule, sorted fact refs, sorted
 * entity ids)`. The same logical issue produces the same id across audit
 * runs, so a GM's dismissal is stable; when the underlying facts change, the
 * id changes and a fresh advisory may fire (dismissal does not silence "the
 * next almost-identical issue"). See §5.1.
 *
 * Messages are NOT baked as English here — the engine emits an i18n
 * `message_key` + `params`, and the rendering surface (client / worker
 * export) resolves it through its locale-aware message function. This keeps
 * the "no hardcoded English" invariant (CLAUDE.md / i18n) intact in a pure,
 * dependency-light package.
 */

/**
 * The four-level severity ladder (§5.1). Only `blocking_error` ever refuses a
 * write; `error` / `warning` / `info` are editorial-confidence signals the GM
 * may always dismiss.
 *
 * - `blocking_error` — corrupts a projection-depended invariant; fix-or-cancel.
 * - `error` — high editorial confidence something is wrong; write still accepted.
 * - `warning` — soft-constraint violation; review, no urgency.
 * - `info` — narrative-opportunity / hygiene notice; collapsed by default.
 */
export type Severity = 'blocking_error' | 'error' | 'warning' | 'info';

/** Severity rank for deterministic ordering (highest first). */
const SEVERITY_RANK: Record<Severity, number> = {
  blocking_error: 0,
  error: 1,
  warning: 2,
  info: 3,
};

/**
 * A per-Campaign override for a rule's default severity (§5.2.1). `'off'`
 * mutes the rule entirely (no advisories surface). Any `Severity` value
 * re-targets the rule — downgrade (`warning → info`), mute (`→ off`), or
 * promote (`info → warning`) for stricter campaigns. `blocking_error` cannot
 * be overridden away — a structural-integrity rule still refuses the write
 * regardless of the override (the override is clamped at engine time).
 */
export type SeverityOverride = Severity | 'off';

/**
 * A reference to one fact in an advisory. Facts have no standalone global id
 * in the fold — they are addressed by `(subject, factKey)` where `factKey` is
 * the `predicate` or `predicate#instance` map key. We join them into a stable
 * string so content-addressing and de-duplication are deterministic.
 */
export function factRef(subject: string, factKey: string): string {
  return `${subject}::${factKey}`;
}

/**
 * A one-click fix the GM can apply to resolve an advisory (§5.1). Modelled as a
 * declarative INTENT, not raw ops — the engine stays pure and op-shape-free; the
 * client translates the intent into its existing op-log mutation path (so every
 * fix appends an op, never bypasses the log). `label_key` + `params` localise
 * the button at the render boundary. Discriminated for future fix kinds.
 *
 * - `remove_fact` — drop one fact (its chain is soft-deleted). Used to clear a
 *   broken reference or break a containment loop. `factKey` is the bare
 *   predicate (the engine only attaches this when there's no instance/belief
 *   suffix, so the client's predicate-keyed soft-delete applies cleanly).
 */
export interface SuggestedFix {
  kind: 'remove_fact';
  subject: string;
  factKey: string;
  label_key: string;
  params: Record<string, string | number>;
}

/**
 * An advisory before its content-addressed id is stamped. Rules return these;
 * `runAudit` computes the id and applies severity overrides.
 */
export interface RawAdvisory {
  severity: Severity;
  rule: string;
  /** i18n key, e.g. `auditor.ref.dangling`. Resolved at the render boundary. */
  message_key: string;
  /** Interpolation params for the message (entity names, values, ids). */
  params: Record<string, string | number>;
  /** Fact references (`factRef`) this advisory concerns. */
  fact_ids: string[];
  /** Entity ids this advisory concerns. */
  entity_ids: string[];
  /** Optional one-click fix (§5.1). Absent → the advisory is informational. */
  fix?: SuggestedFix;
}

/** A fully-stamped advisory (content-addressed id applied). */
export interface Advisory extends RawAdvisory {
  /** Content-addressed: `hash(rule, sorted fact_ids, sorted entity_ids)`. */
  id: string;
}

/**
 * FNV-1a 32-bit, returned as zero-padded hex. Deterministic, dependency-free,
 * and stable across runs/processes — exactly what content-addressing needs
 * (we are not defending against adversarial collisions, only producing a
 * stable digest of a rule + its participants).
 */
export function fnv1a(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    // hash *= 16777619, kept in 32-bit space via the >>> 0 below.
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/**
 * Content-addressed advisory id (§5.1): `hash(rule, sorted fact_ids, sorted
 * entity_ids)`. Sorting makes the id independent of the order a rule happens
 * to collect participants. The `rule|…|…` separators keep distinct field
 * boundaries unambiguous.
 */
export function advisoryId(rule: string, factIds: string[], entityIds: string[]): string {
  const facts = [...factIds].sort().join(',');
  const entities = [...entityIds].sort().join(',');
  return fnv1a(`${rule}|${facts}|${entities}`);
}

/** Stamp a raw advisory with its content-addressed id. */
export function stampAdvisory(raw: RawAdvisory): Advisory {
  return { ...raw, id: advisoryId(raw.rule, raw.fact_ids, raw.entity_ids) };
}

/**
 * Deterministic ordering for an advisory list: severity (most severe first),
 * then rule id, then advisory id. Stable for snapshot tests and a stable GM
 * inbox order.
 */
export function compareAdvisories(a: Advisory, b: Advisory): number {
  const sev = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
  if (sev !== 0) return sev;
  if (a.rule !== b.rule) return a.rule < b.rule ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}
